import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, StatCard, Table, useTable, Select } from "@repo/o37-ui";
import {
  Plus,
  TrendingUp,
  Upload,
  Building,
  Calendar,
  Edit2,
  Trash2,
  BarChart3,
  Target
} from "lucide-react";
import { HoldingsRevenue, HoldingsCompany, formatCents } from '../../types';
import RevenueForm from '../../components/Holdings/RevenueForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function RevenueList() {
  const navigate = useNavigate();
  const [revenues, setRevenues] = useState<HoldingsRevenue[]>([]);
  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState<HoldingsRevenue | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });

  // Filters
  const [filterCompany, setFilterCompany] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [revenueRes, companiesRes] = await Promise.all([
        fetch(`${API_URL}/holdings/revenue`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/companies`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (revenueRes.ok) {
        const data = await revenueRes.json();
        setRevenues(data.map((r: any) => ({
          id: r.id,
          companyId: r.company_id,
          periodType: r.period_type,
          periodStart: r.period_start,
          periodEnd: r.period_end,
          revenueCents: r.revenue_cents,
          notes: r.notes,
          source: r.source,
          createdBy: r.created_by,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
          company: r.holdings_companies ? {
            id: r.holdings_companies.id,
            name: r.holdings_companies.name
          } : undefined
        })));
      }

      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setCompanies(data.map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status
        })));
      }
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this revenue record?')) return;

    try {
      const response = await fetch(`${API_URL}/holdings/revenue/${id}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        loadData();
      }
    } catch (e) {
      console.error('Failed to delete revenue:', e);
    }
  };

  const formatPeriod = (periodStart: string, _periodEnd: string, periodType: string) => {
    const start = new Date(periodStart);
    if (periodType === 'month') {
      return start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else {
      const quarter = Math.ceil((start.getMonth() + 1) / 3);
      return `Q${quarter} ${start.getFullYear()}`;
    }
  };

  // Get unique years from revenue data
  const years = useMemo(() => {
    const yearSet = new Set<string>();
    revenues.forEach(r => {
      const year = new Date(r.periodStart).getFullYear().toString();
      yearSet.add(year);
    });
    // Add current year if not present
    yearSet.add(new Date().getFullYear().toString());
    return Array.from(yearSet).sort((a, b) => parseInt(b) - parseInt(a));
  }, [revenues]);

  // Filter revenues
  const filteredRevenues = useMemo(() => {
    return revenues.filter(r => {
      const year = new Date(r.periodStart).getFullYear().toString();
      if (filterYear && year !== filterYear) return false;
      if (filterCompany && r.companyId !== filterCompany) return false;
      return true;
    });
  }, [revenues, filterYear, filterCompany]);

  // Calculate totals
  const totalRevenue = filteredRevenues.reduce((sum, r) => sum + r.revenueCents, 0);
  const companiesWithRevenue = new Set(filteredRevenues.map(r => r.companyId)).size;
  const periodsRecorded = filteredRevenues.length;

  // Calculate year-over-year if possible
  const currentYearRevenue = revenues
    .filter(r => new Date(r.periodStart).getFullYear().toString() === filterYear)
    .reduce((sum, r) => sum + r.revenueCents, 0);
  const prevYear = (parseInt(filterYear) - 1).toString();
  const prevYearRevenue = revenues
    .filter(r => new Date(r.periodStart).getFullYear().toString() === prevYear)
    .reduce((sum, r) => sum + r.revenueCents, 0);
  const yoyGrowth = prevYearRevenue > 0
    ? ((currentYearRevenue - prevYearRevenue) / prevYearRevenue * 100).toFixed(1)
    : null;

  const columns = useMemo(() => [
    {
      id: "company",
      header: "Company",
      cell: ({ row }: { row: { original: HoldingsRevenue } }) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
            <Building className="w-5 h-5 text-neutral-500" />
          </div>
          <span className="font-medium text-neutral-900">
            {row.original.company?.name || 'Unknown'}
          </span>
        </div>
      )
    },
    {
      id: "period",
      header: "Period",
      cell: ({ row }: { row: { original: HoldingsRevenue } }) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-900">
            {formatPeriod(row.original.periodStart, row.original.periodEnd, row.original.periodType)}
          </span>
          <span className="text-xs text-neutral-500">
            ({row.original.periodType})
          </span>
        </div>
      )
    },
    {
      id: "revenue",
      header: "Revenue",
      cell: ({ row }: { row: { original: HoldingsRevenue } }) => (
        <span className="font-semibold text-emerald-600">
          {formatCents(row.original.revenueCents)}
        </span>
      )
    },
    {
      id: "source",
      header: "Source",
      cell: ({ row }: { row: { original: HoldingsRevenue } }) => (
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
          row.original.source === 'manual' ? 'bg-blue-100 text-blue-700' :
          row.original.source === 'import' ? 'bg-purple-100 text-purple-700' :
          'bg-neutral-100 text-neutral-700'
        }`}>
          {row.original.source || 'manual'}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }: { row: { original: HoldingsRevenue } }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-8 text-xs px-2"
            onClick={() => {
              setEditingRevenue(row.original);
              setShowForm(true);
            }}
            icon={<Edit2 className="w-3.5 h-3.5" />}
            text=""
          />
          <Button
            variant="outline"
            className="h-8 text-xs px-2 text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => handleDelete(row.original.id)}
            icon={<Trash2 className="w-3.5 h-3.5" />}
            text=""
          />
        </div>
      )
    }
  ], []);

  const { table, ...tableProps } = useTable({
    data: filteredRevenues,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: filteredRevenues.length,
    resourceName: (plural) => `record${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Revenue</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Revenue Tracking</h1>
          <p className="text-neutral-500">Track revenue by company and period</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/holdings/revenue/forecasts')}
            icon={<Target className="w-4 h-4" />}
            text="Forecasts"
          />
          <Button
            variant="outline"
            onClick={() => navigate('/holdings/revenue/margins')}
            icon={<BarChart3 className="w-4 h-4" />}
            text="Margins"
          />
          <Button
            variant="outline"
            onClick={() => navigate('/holdings/revenue/import')}
            icon={<Upload className="w-4 h-4" />}
            text="Bulk Import"
          />
          <Button
            onClick={() => {
              setEditingRevenue(null);
              setShowForm(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            text="Add Revenue"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label={`${filterYear} Revenue`}
          value={formatCents(totalRevenue)}
          hint="Total recorded"
        />
        <StatCard
          label="Companies"
          value={companiesWithRevenue.toString()}
          hint="With revenue data"
        />
        <StatCard
          label="Records"
          value={periodsRecorded.toString()}
          hint="Period entries"
        />
        <StatCard
          label="YoY Growth"
          value={yoyGrowth ? `${yoyGrowth}%` : 'N/A'}
          hint={yoyGrowth ? `vs ${prevYear}` : 'Insufficient data'}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-48">
          <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
            <option value="">All Years</option>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        </div>
        <div className="w-48">
          <Select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* Revenue Table */}
      <Card>
        <CardContent className="p-0">
          {filteredRevenues.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <h3 className="text-lg font-medium mb-2">No Revenue Records</h3>
              <p className="mb-6">
                {filterYear || filterCompany
                  ? 'No records match your filters.'
                  : 'Start tracking revenue for your portfolio companies.'}
              </p>
              <Button
                onClick={() => {
                  setEditingRevenue(null);
                  setShowForm(true);
                }}
                icon={<Plus className="w-4 h-4" />}
                text="Add Revenue"
              />
            </div>
          ) : (
            <Table {...tableProps} table={table} />
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h3 className="font-semibold text-neutral-900 mb-3">About Revenue Tracking</h3>
          <div className="text-sm text-neutral-600 space-y-2">
            <p>
              Track monthly or quarterly revenue for each portfolio company. This data feeds into
              margin analysis to understand profitability across your holdings.
            </p>
            <p>
              Use the bulk import feature to upload historical data or sync from external systems.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Form Modal */}
      {showForm && (
        <RevenueForm
          revenue={editingRevenue}
          companies={companies}
          onClose={() => {
            setShowForm(false);
            setEditingRevenue(null);
          }}
          onSaved={() => {
            setShowForm(false);
            setEditingRevenue(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
